import { capitalize } from 'lodash';
import { useIntl } from 'react-intl';
import { Input, Selector, SelectorOptionPropsInterface, useQueryFetch } from 'sonata-react-components';
import {
  AudienceFirstProviderEnum,
  AudienceThirdPartyProviderEnum
} from 'sonata-static-storage/lib/schemas/user/userAudienceConfigurationSchema';

import { getAudienceMappings, postDataSegment } from '@/resources/audienceTargeting.resources';
import { ProfileSettingsStyled } from './ProfileSettings.styles';

type Settings = Parameters<typeof postDataSegment>['0'];

type Props = {
  onChange: (values: Partial<Settings>) => void;
};

function ProfileSettings({ onChange }: Props) {
  const { formatMessage } = useIntl();
  const audienceMappingToken = {};

  const unionOfProviders = [
    ...new Set([...Object.keys(AudienceFirstProviderEnum), ...Object.keys(AudienceThirdPartyProviderEnum)])
  ];
  const providerOptions = unionOfProviders.map(provider => {
    return {
      id: provider,
      label: capitalize(provider),
      value: provider
    };
  });

  const countryOptions = Object.keys(taptap.allCountries)
    .filter(countryCode => countryCode !== 'default')
    .map(countryCode => {
      return {
        id: countryCode,
        label: taptap.allCountries[countryCode].name,
        value: countryCode.toUpperCase()
      };
    });

  const { data: audienceCategoryOptions = [] } = useQueryFetch('audience_profile', async () => {
    const audienceMapping = await getAudienceMappings(audienceMappingToken);
    const audienceMappingFiltered = audienceMapping?.filter((a: any) => a.audienceCategoryIds.length);

    return audienceMappingFiltered.map((a: any) => ({
      id: a.audienceCategoryIds.join(),
      label: a.name
    }));
  });

  const handleNameChange = (newValue: string) => {
    onChange({ name: newValue });
  };

  const handleProvidersChange = (newValue: SelectorOptionPropsInterface[]) => {
    const value = newValue.map(n => n.value) as string[];
    onChange({ providers: value });
  };

  const handleCountriesChange = (newValue: SelectorOptionPropsInterface[]) => {
    const value = newValue.map(n => n.value) as string[];
    onChange({ countries: value });
  };

  const handleAudienceCategoriesChange = (newValue: SelectorOptionPropsInterface) => {
    const value = newValue.id as string;
    onChange({ categories: value.split(',') });
  };

  return (
    <ProfileSettingsStyled className="profileSettings">
      <Input
        disabled={false}
        id="audienceProfile_profileSettings"
        inputAdditionalProps={{}}
        label={formatMessage({
          defaultMessage: 'Audience Profile Name',
          id: 'audienceProfile.steps.form.input.label'
        })}
        name="name"
        onChange={newValue => handleNameChange(newValue as string)}
        placeholder={formatMessage({
          defaultMessage: 'Profile name',
          id: 'audienceProfile.steps.form.input.placeholder'
        })}
        required
        shape="squareUI"
        type="text"
      />
      <div className="profileSettings__selectors">
        <Selector
          label={formatMessage({
            defaultMessage: 'Provider',
            id: 'audienceProfile.steps.form.providerSelector.label'
          })}
          onChange={handleProvidersChange}
          options={providerOptions}
          placeholder={formatMessage({
            defaultMessage: 'Select Provider',
            id: 'audienceProfile.steps.form.providerSelector.placeholder'
          })}
          required
          type="multiple"
        />
        <Selector
          label={formatMessage({
            defaultMessage: 'Country',
            id: 'audienceProfile.steps.form.countrySelector.label'
          })}
          onChange={handleCountriesChange}
          options={countryOptions}
          placeholder={formatMessage({
            defaultMessage: 'Select Country',
            id: 'audienceProfile.steps.form.countrySelector.placeholder'
          })}
          required
          type="multiple"
        />
        <Selector
          label={formatMessage({
            defaultMessage: 'Audience Category',
            id: 'audienceProfile.steps.form.audienceCategorySelector.label'
          })}
          onChange={handleAudienceCategoriesChange}
          options={audienceCategoryOptions}
          placeholder={formatMessage({
            defaultMessage: 'Select Audience Category',
            id: 'audienceProfile.steps.form.audienceCategorySelector.placeholder'
          })}
          required
          type="simple"
        />
      </div>
    </ProfileSettingsStyled>
  );
}

export default ProfileSettings;
